import { getTitleClass, getTitleStyle, getTextShadow, hexToRgba } from '../utils';

const TITLE_DECORATIONS = {
    line: (accent) => <div className="w-16 h-1 rounded-full mx-auto mt-4" style={{ backgroundColor: accent }} />,
    dots: (accent) => (
        <div className="flex justify-center gap-1.5 mt-4">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
            <span className="w-1.5 h-1.5 rounded-full opacity-50" style={{ backgroundColor: accent }} />
            <span className="w-1 h-1 rounded-full opacity-30" style={{ backgroundColor: accent }} />
        </div>
    ),
    icon: (accent) => (
        <div className="flex justify-center mt-4">
            <svg className="w-5 h-5" style={{ color: accent }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        </div>
    ),
    none: () => null,
};

const LETTER_SPACING = {
    tight: 'tracking-tight',
    wide: 'tracking-wide',
    wider: 'tracking-[0.15em]',
};

const SUBTITLE_SIZES = {
    sm: 'text-sm md:text-base',
    lg: 'text-lg md:text-xl',
};

function parseHighlightedTitle(title, accentColor) {
    const regex = /\*\*(.+?)\*\*/g;
    if (!regex.test(title)) return title;
    regex.lastIndex = 0;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(title)) !== null) {
        if (match.index > lastIndex) {
            parts.push(title.slice(lastIndex, match.index));
        }
        parts.push(
            <span key={match.index} className="relative inline-block px-2 py-0.5" style={{
                backgroundColor: hexToRgba(accentColor, 0.2),
                transform: 'rotate(-1deg)',
                display: 'inline-block',
                borderRadius: '0.25rem',
            }}>{match[1]}</span>
        );
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < title.length) {
        parts.push(title.slice(lastIndex));
    }
    return parts;
}

export default function SectionHeader({ tag, title, subtitle, config = {}, s = {} }) {
    const titleClass = getTitleClass(config);
    const decoration = TITLE_DECORATIONS[config.title_decoration || 'line'];
    const letterSpacing = LETTER_SPACING[config.letter_spacing] || '';
    const subtitleSize = SUBTITLE_SIZES[config.subtitle_size] || 'text-base md:text-lg';

    const gradientFrom = config.gradient_from || s.accent;
    const gradientTo = config.gradient_to || s.secondary || s.accent;

    const titleStyle = {
        ...(config.title_style ? getTitleStyle(config, s.accent) : { color: s.text }),
        ...(config.text_shadow ? { textShadow: getTextShadow(config.text_shadow, s.accent) } : {}),
        ...(config.gradient_title ? {
            background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            ...(config.text_effect === 'gradient-animate' ? {
                backgroundSize: '200% 200%',
                animation: 'lmGradTextAnim 4s ease infinite',
            } : {}),
        } : {}),
    };

    const renderedTitle = config.text_highlight && typeof title === 'string'
        ? parseHighlightedTitle(title, s.accent)
        : title;

    const subtitleStyle = config.subtitle_gradient
        ? {
            backgroundImage: `linear-gradient(135deg, ${s.accent}, ${s.secondary || s.accent})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        }
        : { color: s.textSub };

    return (
        <div className="text-center mb-12 md:mb-14">
            {tag && <span className="inline-block text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: s.secondary }}>{tag}</span>}
            {title && (
                <h2 className={`${titleClass} font-bold ${tag ? '' : 'mt-0'} ${letterSpacing}`}
                    style={titleStyle}>
                    {renderedTitle}
                </h2>
            )}
            {subtitle && <p className={`mt-4 max-w-2xl mx-auto ${subtitleSize} leading-relaxed`} style={subtitleStyle}>{subtitle}</p>}
            {decoration && decoration(s.accent)}
            {config.gradient_title && config.text_effect === 'gradient-animate' && <style>{`@keyframes lmGradTextAnim{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}`}</style>}
        </div>
    );
}
